import { type ComponentType } from 'react'

export interface ArticleMeta {
	title: string
	date: string
	description: string
	slug: string
	/**
	 * Required in every article. Opt-OUT semantics — see the filter in
	 * getAllArticles: an absent flag is falsy and therefore PUBLISHES. Enforced by
	 * articles.test.ts, not by tsc (.mdx files are not typechecked).
	 */
	draft: boolean
	/**
	 * The "Hard problem" / "Approach" capsule rendered at the top of every post by
	 * ArticleLayout — the five-second version for a skim-reader, and the recruiting
	 * lens the site is built around ("what hard problems have you solved?").
	 *
	 * These live in metadata rather than as `<ProblemCapsule>` in the MDX body so a
	 * single post can't quietly ship without one, and so other surfaces (the
	 * /writing index, OG cards) can read the pair. Required in every article and
	 * enforced by articles.test.ts, not by tsc (.mdx files are not typechecked).
	 */
	hardProblem: string
	approach: string
	tags?: string[]
}

export interface ArticleWithSlug extends ArticleMeta {
	/** The default export of the compiled MDX module (already wraps ArticleLayout). */
	Component: ComponentType<Record<string, unknown>>
}

interface MdxModule {
	default: ComponentType<Record<string, unknown>>
	article: ArticleMeta
}

// Eager glob: every post is compiled into the bundle so it can be resolved
// synchronously at render time (a React component can't cross the
// loader -> component serialization boundary, so we never return it from a
// loader — routes re-resolve via getAllArticles() in render).
const modules = import.meta.glob<MdxModule>('/content/writing/*.mdx', {
	eager: true,
})

function slugFromPath(filePath: string): string {
	return filePath.replace(/.*\/([^/]+)\.mdx$/, '$1')
}

export function getAllArticles(): ArticleWithSlug[] {
	return (
		Object.entries(modules)
			.map(([filePath, mod]) => ({
				...mod.article,
				slug: mod.article.slug ?? slugFromPath(filePath),
				Component: mod.default,
			}))
			// Drafts render in dev (`npm run dev`) so work-in-progress is previewable,
			// but are excluded from the production build (`react-router build`).
			.filter((article) => import.meta.env.DEV || !article.draft)
			.sort((a, z) => +new Date(z.date) - +new Date(a.date))
	)
}

export function getArticleBySlug(slug: string): ArticleWithSlug | undefined {
	return getAllArticles().find((article) => article.slug === slug)
}
